// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PersonLabel } from '@/components/person-label';
import { Ticket } from '@/ticket/api/types';
import { TicketAssignToControl } from '@/ticket/components/ticket-assign-to-control';
import { TicketCategoryControl } from '@/ticket/components/ticket-category-control';
import { TICKET_STATUS_DISPLAY_MAP } from '@/ticket/util/labels';
import { Box, Card, Chip, Grid, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

interface Props {
  /**
   * The ticket to display details for
   */
  ticket: Ticket;
}

/**
 * Displays the details section of a ticket, including metadata and timestamps.
 */
export function TicketDetailsCard({ ticket }: Props) {
  const formatDateTime = (date: DateTime) => {
    return date.toLocaleString(DateTime.DATETIME_MED);
  };
  return (
    <Card variant="outlined">
      <Stack spacing={1} p={3}>
        <Typography variant="h6">Details</Typography>
        <Grid container spacing={2}>
          {/* First column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Assigned to
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <TicketAssignToControl ticket={ticket} />
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Status
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Chip
                    label={TICKET_STATUS_DISPLAY_MAP[ticket.status].label}
                    color={TICKET_STATUS_DISPLAY_MAP[ticket.status].color}
                    size="small"
                  />
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Category
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <TicketCategoryControl ticket={ticket} />
                </Box>
              </Stack>
            </Stack>
          </Grid>
          {/* Second column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Created by
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <PersonLabel name={ticket.createdBy} />
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Updated by
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <PersonLabel name={ticket.lastUpdatedBy} />
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Closed by
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <PersonLabel name={ticket.closedBy} />
                </Box>
              </Stack>
            </Stack>
          </Grid>
          {/* Third column */}
          <Grid item xs={12} md={4}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Created at
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography>{formatDateTime(ticket.createdAt)}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Updated at
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography>
                    {formatDateTime(ticket.lastUpdatedAt)}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" minHeight={32}>
                <Typography color="text.secondary" sx={{ width: 120 }}>
                  Closed at
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    color={ticket.closedAt ? 'text.primary' : 'text.secondary'}
                  >
                    {ticket.closedAt
                      ? formatDateTime(ticket.closedAt)
                      : 'Not set'}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );
}
